import React, { useRef, useEffect, useState } from 'react';

import { Product } from '../hooks/useProducts';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectIsProductConfirmed,
  selectSelectedProductVariants,
  selectIsProductConfirmationMode,
} from '../store/selectors';
import { setIsProductConfirmationMode } from '../store/slices/confirmationSlice';
import { formatPrice } from '../utils/Formatting';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import { useSession } from '../hooks/useSession';
import { ApiManager } from '../services/ApiManager';
import './SelectedProductCompactView.css';
import closeIcon from '../assets/close.png';
import diamondIcon from '../assets/diamond.png';

interface SelectedProductCompactViewProps {
  product: Product;
  walletAmount: number;
}

const SelectedProductCompactView: React.FC<SelectedProductCompactViewProps> = ({ product, walletAmount }) => {
  const isProductConfirmed = useAppSelector(selectIsProductConfirmed);

  const dispatch = useAppDispatch();
  const isProductConfirmationMode = useAppSelector(selectIsProductConfirmationMode);
  const selectedVariants = useAppSelector(selectSelectedProductVariants);
  const { sessionId } = useSession();
  const discountElementRef = useRef<HTMLSpanElement>(null);
  const [discountPulse, setDiscountPulse] = React.useState(false);
  const [animatedDiscountAmount, setAnimatedDiscountAmount] = useState(0);
  const [flyingDiscounts, setFlyingDiscounts] = React.useState<Array<{
    key: string;
    id: string;
    imageUrl: string;
    top: number;
    left: number;
    targetX: number;
    targetY: number;
  }> | null>(null);

  // Translation labels
  const { settings } = useSettingsRedux();
  const productTranslations = (settings?.translations?.product ?? {}) as Record<string, string>;
  const labelPrice = productTranslations.price ?? 'Price';
  const labelDiscount = productTranslations.discount ?? 'Discount';
  const labelTotal = productTranslations.total ?? 'Total';

  // Add useEffect to handle synchronized discount animation
  useEffect(() => {
    if (isProductConfirmationMode && !isProductConfirmed) {
      // Start the discount count-up animation with 500ms delay (synchronized with wallet countdown)
      setAnimatedDiscountAmount(0);

      if (walletAmount > 0) {
        const delayTimeout = setTimeout(() => {
          const interval = setInterval(() => {
            setAnimatedDiscountAmount((prev) => {
              const next = prev + 1;
              if (next >= walletAmount) {
                clearInterval(interval);
                return walletAmount;
              }
              return next;
            });
          }, 75);
        }, 1500);

        return () => {
          clearTimeout(delayTimeout);
        };
      }
    } else {
      // Reset animated discount when not in confirmation mode
      setAnimatedDiscountAmount(0);
    }
  }, [isProductConfirmationMode, isProductConfirmed, walletAmount]);

  // Manual animation function that finds the wallet element
  const triggerWalletToDiscountAnimation = React.useCallback(() => {
    // Find wallet element by class name instead of using ref
    const walletElement = document.querySelector('.playmerce-game-statusbar-wallet') as HTMLElement;
    const discountElement = discountElementRef.current;

    if (!walletElement || !discountElement) {
      console.warn('Wallet or discount element not found for animation', {
        walletElement: !!walletElement,
        discountElement: !!discountElement,
      });
      return;
    }

    const walletRect = walletElement.getBoundingClientRect();
    const discountRect = discountElement.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Calculate start position (wallet center)
    const walletCenterX = walletRect.left + walletRect.width / 2;
    const walletCenterY = walletRect.top + walletRect.height / 2 - 30;

    // Calculate target position (discount element center)
    const discountCenterX = discountRect.left + discountRect.width / 2;
    const discountCenterY = discountRect.top + discountRect.height / 2 + scrollTop;

    // Calculate translation vector from wallet to discount
    const targetX = discountCenterX - walletCenterX;
    const targetY = discountCenterY - walletCenterY - 30;

    // Create flying discounts with staggered animations
    const newFlyingDiscounts = [
      {
        key: `discount-1-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY,
        left: walletCenterX,
        targetX,
        targetY,
        zIndex: 99999,
      },
      {
        key: `discount-2-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY + 3,
        left: walletCenterX + 3,
        targetX,
        targetY,
        zIndex: 99999,
      },
      {
        key: `discount-3-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY - 3,
        left: walletCenterX - 3,
        targetX,
        targetY,
        zIndex: 99999,
      },
    ];

    // Start the animations
    setFlyingDiscounts(newFlyingDiscounts);

    // Animate elements once they're rendered using Web Animations API
    setTimeout(() => {
      const animationPromises: Promise<void>[] = [];

      newFlyingDiscounts.forEach((discount, index) => {
        const element = document.querySelector(`[data-flying-key="${discount.key}"]`) as HTMLElement;
        if (element) {
          const delay = index * 150; // 150ms stagger
          const duration = index === 1 ? 700 : index === 2 ? 900 : 800;

          const animationPromise = new Promise<void>((resolve) => {
            const animation = element.animate(
              [
                {
                  transform: 'translate(0, 0) scale(1)',
                  opacity: '1',
                },
                {
                  transform: `translate(${discount.targetX}px, ${discount.targetY}px) scale(0.6)`,
                  opacity: '1',
                  offset: 0.8,
                },
                {
                  transform: `translate(${discount.targetX}px, ${discount.targetY}px) scale(0.6)`,
                  opacity: '0',
                },
              ],
              {
                duration,
                delay,
                easing: 'ease-in-out',
                fill: 'forwards',
              }
            );

            animation.addEventListener('finish', () => resolve());
          });

          animationPromises.push(animationPromise);
        }
      });

      // Wait for all animations to complete, then trigger discount pulse
      Promise.all(animationPromises).then(() => {
        setDiscountPulse(true);
        setTimeout(() => setDiscountPulse(false), 400);
      });
    }, 16); // Next frame

    // Clear flying discounts after all animations complete
    setTimeout(() => setFlyingDiscounts(null), 1200);
  }, []);

  // Trigger animation when component mounts
  useEffect(() => {
    // Delay to ensure the compact view transition is complete and elements are fully rendered
    // The compact view appears after 450ms transition, so we add significant delay for the user to see the compact view
    const timer = setTimeout(() => {
      triggerWalletToDiscountAnimation();
    }, 1000); // 1 second delay after compact view appears to let user see the view first

    return () => clearTimeout(timer);
  }, [triggerWalletToDiscountAnimation]);

  const handleCancel = () => {
    dispatch(setIsProductConfirmationMode(false));

    // Send analytics event for product back action
    const sendAnalytics = async () => {
      try {
        await ApiManager.analytics.playmerce_product_back(sessionId || undefined);
      } catch (error) {
        console.error('Failed to send product back analytics:', error);
      }
    };

    sendAnalytics();
  };

  const originalPrice = parseFloat(product.priceRange.minVariantPrice.amount);
  const calculatedSubtotal = Math.max(0, originalPrice - animatedDiscountAmount);
  const currencyCode = product.priceRange.minVariantPrice.currencyCode;

  // Helper function to format variants for display
  const formatVariantsDisplay = () => {
    if (!selectedVariants || Object.keys(selectedVariants).length === 0) {
      return null;
    }

    return Object.entries(selectedVariants)
      .filter(([key, value]) => key !== 'Title')
      .map(([key, value]) => (
        <span key={key} className="compact-variant-item">
          {key}: {value}
        </span>
      ));
  };

  // Helper function to get product ID as number
  const getProductId = (): number => {
    // Extract numeric ID from product.id string
    const numericId = product.id.match(/\d+/);
    return numericId ? parseInt(numericId[0], 10) : 0;
  };

  // Helper function to get variant data for analytics
  const getVariantData = () => {
    // Get the first variant (primary variant) or default values
    const firstVariant = product.variants?.edges?.[0]?.node;

    if (firstVariant) {
      const variantNumericId = firstVariant.id.match(/\d+/);
      return {
        variant_id: variantNumericId ? parseInt(variantNumericId[0], 10) : 0,
        variant_title: firstVariant.title || 'Default',
        price: {
          amount: parseFloat(firstVariant.price || '0'),
          currency: product.priceRange.minVariantPrice.currencyCode || 'EUR',
        },
      };
    }

    // Fallback to product price range data
    return {
      variant_id: 0,
      variant_title: 'Default',
      price: {
        amount: parseFloat(product.priceRange.minVariantPrice.amount || '0'),
        currency: product.priceRange.minVariantPrice.currencyCode || 'EUR',
      },
    };
  };

  // Send analytics event when compact product view is shown
  useEffect(() => {
    if (product && sessionId) {
      const sendAnalytics = async () => {
        try {
          const variantData = getVariantData();

          await ApiManager.analytics.playmerce_product_add(
            {
              product_id: getProductId(),
              product_title: product.title,
              variant_id: variantData.variant_id,
              variant_title: variantData.variant_title,
              price: variantData.price,
            },
            sessionId
          );
        } catch (error) {
          console.error('Failed to send product add analytics:', error);
        }
      };

      sendAnalytics();
    }
  }, []); // Empty dependency array - only run once when component mounts

  // Early return AFTER all hooks to ensure consistent hook calls
  if (!product || isProductConfirmed) {
    return null; // Hide the component when product is confirmed
  }

  return (
    <>
      <div className="selected-product-compact-view">
        <button id="cancel-button" className="cancel-button text-button" onClick={handleCancel}>
          <img src={closeIcon} alt="Close" />
        </button>
        <div className="compact-product-details-row">
          <div className="compact-product-image-container">
            {product.featuredImage ? (
              <img
                src={product.featuredImage.url}
                alt={product.featuredImage.altText || product.title}
                className="compact-product-image"
              />
            ) : (
              <div className="compact-no-image-placeholder"></div>
            )}
          </div>
          <div className="compact-product-info">
            <h3 className="compact-product-title">{product.title}</h3>
            {selectedVariants && Object.keys(selectedVariants).length > 0 && (
              <div className="compact-product-variants">{formatVariantsDisplay()}</div>
            )}
            <div className="compact-product-price">
              <p className="compact-product-price-row compact-product-price-original">
                {formatPrice(
                  product.priceRange.minVariantPrice.amount,
                  product.priceRange.minVariantPrice.currencyCode
                )}
              </p>
              <p className="compact-product-price-row">
                <span className="compact-product-discount">{labelDiscount}: </span>
                <span ref={discountElementRef} className={`compact-product-discount`}>
                  -{formatPrice(animatedDiscountAmount.toString(), currencyCode)}
                </span>
              </p>
              <p className="compact-product-price-row compact-product-price-total">
                {labelPrice}: {formatPrice(calculatedSubtotal.toString(), currencyCode)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Render flying discounts for this component */}
      {flyingDiscounts &&
        flyingDiscounts.map((discount) => (
          <img
            key={discount.key}
            data-flying-key={discount.key}
            src={discount.imageUrl}
            alt=""
            className="flying-discount"
            style={{
              top: `${discount.top}px`,
              left: `${discount.left}px`,
            }}
          />
        ))}
    </>
  );
};

export default SelectedProductCompactView;
